import { useEffect } from 'react';
import { Provider, useDispatch, useSelector } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';

import { useAuth0 } from '@auth0/auth0-react';
import { ThemeProvider as ThemeProviderMui } from '@mui/material/styles';

import { useGetFeaturesQuery, useGetMinersDataQuery, useGetUserDataQuery } from 'api';
import { useGetProductsDataQuery } from 'api/product-api';
import AppRouter from 'AppRouter';
import {
  AppNotification,
  Footer,
  Header,
  Loader,
  NoApiImplementedModal,
  NoConnectedMinersModal,
  RootLayout,
  TopUpBalanceModal,
  UserInformationMissingModal,
} from 'shared';
import { DunningBanner } from 'shared/DunningBanner';
import { AnnouncementsModal } from 'shared/Modals/AnnouncementsModal';
import { CampaignControllerModal } from 'shared/Modals/CampaignControllerModal';
import { RootState, setupStore } from 'store';
import configSelector from 'store/config/config.selectors';
import { setMinersData } from 'store/slices/miners-slice';
import { setProducts } from 'store/slices/products-slice';
import { setUserCurrency, setUserData, userBaseCurrencySelector, userSelector } from 'store/slices/user-slice';
import { applyServerTheme } from 'themes/deriveTheme';
import { useMuiTheme } from 'themes/themeMuiProvider';
import { getBaseTheme, ThemeProvider, useTheme } from 'themes/themeProvider';
import { Currency, ThemeKey } from 'types/general.types';
import { useApp } from 'useApp';

const App = () => {
  const dispatch = useDispatch();

  const { isAuthenticated, isLoading: isAuthLoading } = useAuth0();
  useApp();

  const idToken = useSelector(userSelector).user?.idToken;
  const isIdTokenReady = !!idToken?.__raw;

  const { data: userData, isLoading: isUserDataLoading } = useGetUserDataQuery(undefined, {
    skip: !isIdTokenReady,
  });

  // Plan 06: org feature flags, fetched once at login into the `features` slice (via
  // extraReducers in store/slices/features-slice.ts). Not included in isAppLoading — the mining
  // guard defaults to visible while this is in flight, so mining orgs render unchanged either way.
  useGetFeaturesQuery(undefined, { skip: !isIdTokenReady });

  const { data: minerData, isLoading: isMinerDataLoading } = useGetMinersDataQuery(undefined, {
    skip: !isIdTokenReady,
  });

  const { data: productData, isLoading: isProductDataLoading } = useGetProductsDataQuery(undefined, {
    skip: !isIdTokenReady,
  });

  // The fiat-transaction, invoice and electricity-cost feeds are deliberately NOT fetched here —
  // they are the heaviest org-wide queries and no globally-mounted component reads them. The
  // pages that render them (MyWallets, InvoicesPage, Dashboard's RunwayCard) mount the query
  // hooks themselves; the results land in their slices via extraReducers. User, miners and
  // products stay at startup because the Header and the global modals read them.

  const baseCurrency = useSelector(userBaseCurrencySelector);
  const tenant = useSelector(configSelector.getTenant);
  const { theme, themeKey } = useTheme();
  const muiTheme = useMuiTheme();

  const showCampaignModal =
    themeKey === ThemeKey.PANTHEON ||
    themeKey === ThemeKey.CRYPTOHALL24_DUERR ||
    themeKey === ThemeKey.CRYPTOHALL24_QUETTA;

  useEffect(() => {
    userData && dispatch(setUserData(userData));
    minerData && dispatch(setMinersData(minerData));
    productData && dispatch(setProducts(productData));

    dispatch(
      setUserCurrency({
        currency: userData?.user.baseCurrency ?? Currency.EUR,
      }),
    );
  }, [dispatch, userData, minerData, productData, baseCurrency, themeKey]);

  useEffect(() => {
    document.body.classList.add(tenant, baseCurrency ?? Currency.EUR);
    return () => document.body.classList.remove(tenant, baseCurrency ?? Currency.EUR);
  }, [tenant, baseCurrency]);

  useEffect(() => {
    document.documentElement.style.setProperty('--font-family', theme.fontFamily);
  }, [theme.fontFamily]);

  const isAppLoading =
    isAuthLoading ||
    (isAuthenticated && (!isIdTokenReady || isUserDataLoading || isMinerDataLoading || isProductDataLoading));

  if (isAppLoading) return <Loader color={muiTheme.loader.color} />;

  return (
    <ThemeProviderMui theme={muiTheme}>
      {isAuthenticated && <Header />}
      {isAuthenticated && <DunningBanner />}
      <AppRouter />
      <AppNotification />
      {isAuthenticated && <Footer />}
      {isAuthenticated && <TopUpBalanceModal />}
      {isAuthenticated && <NoConnectedMinersModal />}
      {isAuthenticated && <NoApiImplementedModal />}
      {isAuthenticated && <UserInformationMissingModal />}
      {isAuthenticated && showCampaignModal && <CampaignControllerModal />}
      {isAuthenticated && <AnnouncementsModal />}
    </ThemeProviderMui>
  );
};

const WrappedApp: React.FC<{ preloadedState?: Partial<RootState> }> = ({ preloadedState }) => {
  const themeName = preloadedState?.config?.clientInfo?.theme ?? ThemeKey.DUCKENERGY;
  const serverTenant = preloadedState?.config?.serverTenant;
  const themeOverride = serverTenant ? applyServerTheme(serverTenant.theme, getBaseTheme(themeName)) : undefined;

  return (
    <Provider store={setupStore(preloadedState)}>
      <RootLayout>
        <BrowserRouter>
          <ThemeProvider themeName={themeName} themeOverride={themeOverride}>
            <App />
          </ThemeProvider>
        </BrowserRouter>
      </RootLayout>
    </Provider>
  );
};

export default WrappedApp;
